import { type ComponentType, type Ref, type SVGAttributes } from "react"
import { cn } from "cn"

export type IconSvgProps = SVGAttributes<SVGElement> & {
  size?: number | "relative"
  strokeWidth?: number
  strokeModifier?: number
}

/** An icon component, such as the default export of `@/components/icons/check`. */
export type IconComponent = ComponentType<IconSvgProps>

/**
 * How primitives such as Button take an icon: the component itself, or the
 * component with props for it.
 *
 *   iconBefore={IconCheck}
 *   iconBefore={{ icon: IconCheck, className: "text-success" }}
 */
export type IconSpec = IconComponent | (IconSvgProps & { icon: IconComponent })

/** Splits an IconSpec into its component and the props to render it with. */
export function resolveIconSpec(spec: IconSpec): {
  Icon: IconComponent
  props: IconSvgProps
} {
  if (typeof spec === "function") return { Icon: spec, props: {} }
  const { icon, ...props } = spec
  return { Icon: icon, props }
}

/**
 * Renders an icon component chosen at runtime:
 *
 *   <Icon as={isPrivate ? IconLock : IconGlobe} size={20} />
 *
 * For a fixed icon, render the component itself (`<IconLock />`).
 */
function Icon({
  as: Component,
  ...props
}: IconSvgProps & { as: IconComponent }) {
  return <Component {...props} />
}

function IconSvg({
  ref,
  viewBox = "0 0 24 24",
  strokeWidth = 1.5,
  strokeModifier = 1,
  size = "relative",
  fill = "none",
  className,
  ...props
}: IconSvgProps & { ref?: Ref<SVGSVGElement> }) {
  const isRelativeSize = size === "relative"
  // Icons are decorative unless the caller labels them.
  const isLabelled =
    props["aria-label"] !== undefined ||
    props["aria-labelledby"] !== undefined ||
    props.role !== undefined
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      {...(!isRelativeSize && {
        width: size,
        height: size,
      })}
      data-slot="icon"
      aria-hidden={isLabelled ? undefined : true}
      focusable="false"
      viewBox={viewBox}
      fill={fill}
      stroke="currentColor"
      strokeWidth={strokeWidth * strokeModifier}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn(
        "shrink-0 transform-cpu",
        isRelativeSize && "size-[1em]",
        className
      )}
      {...props}
      ref={ref}
    />
  )
}

IconSvg.displayName = "IconSvg"

export { Icon, IconSvg }
